import { useRef, useState } from "react";
import { FileImage, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AttendanceReportTable } from "@/components/reports/attendance-report-table";
import type { AttendanceReport } from "@/lib/reports/build-report";
import {
  downloadReportAsJpeg,
  downloadReportAsPdf,
} from "@/lib/reports/download";

interface ReportDownloadButtonsProps {
  report: AttendanceReport | null;
  filenameBase: string;
  disabled?: boolean;
}

export function ReportDownloadButtons({
  report,
  filenameBase,
  disabled,
}: ReportDownloadButtonsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<"pdf" | "jpeg" | null>(null);

  async function run(kind: "pdf" | "jpeg") {
    if (!report || !ref.current) return;
    setBusy(kind);
    try {
      if (kind === "pdf") {
        await downloadReportAsPdf(ref.current, filenameBase);
      } else {
        await downloadReportAsJpeg(ref.current, filenameBase);
      }
    } catch (e) {
      console.error(e);
      window.alert("Could not generate report. Try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || !report || busy !== null}
          onClick={() => run("pdf")}
        >
          {busy === "pdf" ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <FileText className="mr-1 h-4 w-4" />
          )}
          PDF
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || !report || busy !== null}
          onClick={() => run("jpeg")}
        >
          {busy === "jpeg" ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <FileImage className="mr-1 h-4 w-4" />
          )}
          JPEG
        </Button>
      </div>

      {/* Off-screen render target for capture */}
      {report && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: -10000,
            top: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <AttendanceReportTable ref={ref} report={report} />
        </div>
      )}
    </>
  );
}
